import { BehaviorSubject, Observable, combineLatest, firstValueFrom, map, share, shareReplay } from 'rxjs';

export enum Theme {
	Auto = 'auto',
	Light = 'light',
	Dark = 'dark',
}

/**
 * Returns @media colour scheme
 * Adjusted from https://tane.dev/2021/01/create-your-own-dark-mode-detection-observable-using-rxjs-and-media-queries/
 */
function prefersDarkMode(signal?: AbortSignal): Observable<boolean> {
	return new Observable<boolean>(subscriber => {

		if (!window.matchMedia) {
			subscriber.next(false); // defaults to light theme
			// subscriber.error(new Error('No windows Media Match available'));
		}

		function emitValue(event: Event) {

			subscriber.next((event as MediaQueryListEvent).matches);
		}

		const mediaListQuery = window.matchMedia('(prefers-color-scheme: dark)');

		if (signal) {
			signal.onabort = () => {
				mediaListQuery.removeEventListener('change', emitValue)
				!subscriber.closed && subscriber.complete()
			}
		}

		mediaListQuery.addEventListener('change', emitValue);
		subscriber.next(mediaListQuery.matches);

		return () => {
			mediaListQuery.removeEventListener('change', emitValue);
			!subscriber.closed && subscriber.complete()
		}
	})
}

const cookieTheme$ = new BehaviorSubject<Theme>(Theme.Auto);

export function setCookieTheme(theme: Theme) {
		cookieTheme$.next(theme);
}

/**
 * @deprecated Replaced with Michiel's button-ey pills
 * A cycle of themes to switch to, depending on system theme
 * @returns auto / light / dark
 */
const nextTheme$: Observable<Theme> = combineLatest([prefersDarkMode(), cookieTheme$]).pipe(
	map(([systemPrefersDark, cookie]) => {
		if (systemPrefersDark) {
			if (cookie === Theme.Light) return Theme.Dark;
			if (cookie === Theme.Dark) return Theme.Auto;
			return Theme.Light;
		}
		if (cookie === Theme.Light) return Theme.Auto;
		if (cookie === Theme.Dark) return Theme.Light;
		return Theme.Dark;
	}),
	share()
);

/**
 * Source of theme-ey truth!
 * The ultimate collaboration of system theme check and cookie
 * To bring you the current dark/light theme
 * @returns light/dark
 */
export const theme$: Observable<Extract<Theme, Theme.Light | Theme.Dark>> = combineLatest([prefersDarkMode(), cookieTheme$]).pipe(
	map(([systemPrefersDark, cookie]) => {
		if (cookie === Theme.Auto) return systemPrefersDark ? Theme.Dark : Theme.Light;
		return cookie;
	}),
	shareReplay(1),
);

export async function getThemeState() {
	return await firstValueFrom(theme$)
}